import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { STARLINK_ENABLED } from "../lib/features";
import {
  Globe,
  LogOut,
  Users,
  FileText,
  BarChart3,
  Settings,
  Download,
  Search,
  Filter,
  Eye,
  Edit,
  Trash2,
  Plus,
  Check,
  X,
  Phone,
  Mail,
  MapPin,
  Package,
  Clock,
  TrendingUp,
  ChevronDown,
  Save,
  ArrowLeft,
  Radio
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth Context
const getToken = () => localStorage.getItem("adminToken");
const setToken = (token) => localStorage.setItem("adminToken", token);
const removeToken = () => localStorage.removeItem("adminToken");

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${getToken()}` }
});

// Status colors
const statusColors = {
  new: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  contacted: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  quoted: "bg-purple-500/20 text-purple-400 border-purple-500/30",
  converted: "bg-green-500/20 text-green-400 border-green-500/30",
  lost: "bg-red-500/20 text-red-400 border-red-500/30"
};

const statusLabels = {
  new: "Nuevo",
  contacted: "Contactado",
  quoted: "Cotizado",
  converted: "Convertido",
  lost: "Perdido"
};

// Login Component
function LoginPage({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/admin/login`, { username, password });
      setToken(response.data.token);
      onLogin();
    } catch (err) {
      setError("Credenciales incorrectas");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="glass rounded-3xl p-8">
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-400 flex items-center justify-center">
              <Globe className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white font-['Outfit']">Global Tech Xperts</h1>
              <p className="text-sm text-neutral-500">Panel de Administración</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm text-neutral-400 mb-2 block">Usuario</label>
              <Input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="admin"
                className="bg-neutral-900/50 border-neutral-800 rounded-xl h-12"
                data-testid="admin-username-input"
              />
            </div>
            <div>
              <label className="text-sm text-neutral-400 mb-2 block">Contraseña</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-neutral-900/50 border-neutral-800 rounded-xl h-12"
                data-testid="admin-password-input"
              />
            </div>

            {error && (
              <div className="p-3 rounded-lg bg-red-500/20 border border-red-500/30 text-red-400 text-sm">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-xl h-12 font-semibold"
              data-testid="admin-login-btn"
            >
              {loading ? "Entrando..." : "Entrar"}
            </Button>
          </form>

          <p className="text-xs text-neutral-600 text-center mt-6">
            Usuario: admin | Contraseña: GlobalTech2024!
          </p>
        </div>
      </motion.div>
    </div>
  );
}

// Dashboard Component
function Dashboard({ stats, onRefresh }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white font-['Outfit']">Dashboard</h2>
        <Button onClick={onRefresh} variant="outline" className="rounded-full border-neutral-700">
          Actualizar
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center">
              <Users className="w-6 h-6 text-orange-400" />
            </div>
            <div>
              <p className="text-sm text-neutral-500">Total Leads</p>
              <p className="text-3xl font-bold text-white">{stats.total_leads || 0}</p>
            </div>
          </div>
        </div>

        <div className="glass rounded-2xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="text-sm text-neutral-500">Conversión</p>
              <p className="text-3xl font-bold text-white">{stats.conversion_rate || 0}%</p>
            </div>
          </div>
        </div>

        <div className="glass rounded-2xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <Clock className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-neutral-500">Nuevos</p>
              <p className="text-3xl font-bold text-white">{stats.by_status?.new || 0}</p>
            </div>
          </div>
        </div>

        <div className="glass rounded-2xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
              <Package className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-neutral-500">Cotizados</p>
              <p className="text-3xl font-bold text-white">{stats.by_status?.quoted || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Distribution */}
        <div className="glass rounded-2xl p-6">
          <h3 className="text-lg font-bold text-white mb-4">Por Estado</h3>
          <div className="space-y-3">
            {Object.entries(stats.by_status || {}).map(([status, count]) => (
              <div key={status} className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${statusColors[status] || 'bg-neutral-500/20 text-neutral-400'}`}>
                  {statusLabels[status] || status}
                </span>
                <div className="flex-1 h-2 bg-neutral-800 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-orange-500 rounded-full"
                    style={{ width: `${(count / (stats.total_leads || 1)) * 100}%` }}
                  />
                </div>
                <span className="text-sm text-neutral-400">{count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Products Distribution */}
        <div className="glass rounded-2xl p-6">
          <h3 className="text-lg font-bold text-white mb-4">Por Producto</h3>
          <div className="space-y-3">
            {Object.entries(stats.by_product || {}).map(([product, count]) => (
              <div key={product} className="flex items-center gap-3">
                <span className="text-sm text-neutral-300 w-20">{product}</span>
                <div className="flex-1 h-2 bg-neutral-800 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-amber-500 rounded-full"
                    style={{ width: `${(count / (stats.total_leads || 1)) * 100}%` }}
                  />
                </div>
                <span className="text-sm text-neutral-400">{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Leads Management Component
function LeadsManager({ leads, onUpdate, onRefresh }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [onlyWithEmail, setOnlyWithEmail] = useState(false);
  const [onlyWithPhone, setOnlyWithPhone] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [editNotes, setEditNotes] = useState("");
  const [editStatus, setEditStatus] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState(null);

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = 
      lead.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.product?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.phone?.includes(searchTerm);
    
    const matchesStatus = statusFilter === "all" || lead.status === statusFilter;

    // "Only with email" — treat empty string / null / whitespace as missing
    const matchesEmail = !onlyWithEmail || !!(lead.email && String(lead.email).trim());
    const matchesPhone = !onlyWithPhone || !!(lead.phone && String(lead.phone).trim());

    return matchesSearch && matchesStatus && matchesEmail && matchesPhone;
  });

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/admin/leads/export/csv`, {
        ...authHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `leads_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error("No se pudo exportar el CSV. Inténtalo de nuevo.");
    }
  };

  const handleUpdateLead = async () => {
    try {
      await axios.put(`${API}/admin/leads/${selectedLead.id}`, {
        status: editStatus,
        notes: editNotes
      }, authHeaders());
      
      setSelectedLead(null);
      onRefresh();
      toast.success("Lead actualizado");
    } catch (err) {
      toast.error("Error al actualizar el lead");
    }
  };

  const handleDeleteLead = async () => {
    try {
      await axios.delete(`${API}/admin/leads/${leadToDelete.id}`, authHeaders());
      setShowDeleteDialog(false);
      setLeadToDelete(null);
      onRefresh();
      toast.success("Lead eliminado");
    } catch (err) {
      toast.error("Error al eliminar el lead");
    }
  };

  const openEditDialog = (lead) => {
    setSelectedLead(lead);
    setEditStatus(lead.status || "new");
    setEditNotes(lead.notes || "");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <h2 className="text-2xl font-bold text-white font-['Outfit']">Leads ({filteredLeads.length})</h2>
        
        <div className="flex flex-wrap gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
            <Input
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-neutral-900/50 border-neutral-800 rounded-xl w-48"
            />
          </div>
          
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 bg-neutral-900/50 border-neutral-800 rounded-xl">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="new">Nuevos</SelectItem>
              <SelectItem value="contacted">Contactados</SelectItem>
              <SelectItem value="quoted">Cotizados</SelectItem>
              <SelectItem value="converted">Convertidos</SelectItem>
              <SelectItem value="lost">Perdidos</SelectItem>
            </SelectContent>
          </Select>

          <Button onClick={handleExportCSV} variant="outline" className="rounded-xl border-neutral-700 gap-2">
            <Download className="w-4 h-4" />
            Exportar CSV
          </Button>
        </div>
      </div>

      {/* Contact-completeness checkboxes */}
      <div className="flex flex-wrap items-center gap-4 -mt-2">
        <label
          className="flex items-center gap-2 text-sm text-neutral-300 cursor-pointer select-none hover:text-white"
          data-testid="filter-only-with-email-label"
        >
          <input
            type="checkbox"
            checked={onlyWithEmail}
            onChange={(e) => setOnlyWithEmail(e.target.checked)}
            data-testid="filter-only-with-email"
            className="accent-lime-400 w-4 h-4"
          />
          <span>📧 Solo leads con email</span>
        </label>
        <label
          className="flex items-center gap-2 text-sm text-neutral-300 cursor-pointer select-none hover:text-white"
          data-testid="filter-only-with-phone-label"
        >
          <input
            type="checkbox"
            checked={onlyWithPhone}
            onChange={(e) => setOnlyWithPhone(e.target.checked)}
            data-testid="filter-only-with-phone"
            className="accent-lime-400 w-4 h-4"
          />
          <span>📞 Solo leads con teléfono</span>
        </label>
        {(onlyWithEmail || onlyWithPhone) && (
          <button
            type="button"
            onClick={() => { setOnlyWithEmail(false); setOnlyWithPhone(false); }}
            data-testid="filter-clear-contact"
            className="text-xs text-neutral-500 hover:text-white underline underline-offset-2"
          >
            Limpiar filtros de contacto
          </button>
        )}
      </div>

      {/* Leads Table */}
      <div className="glass rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-neutral-800/50">
              <tr>
                <th className="text-left p-4 text-sm font-semibold text-neutral-400">Nombre</th>
                <th className="text-left p-4 text-sm font-semibold text-neutral-400">Producto</th>
                <th className="text-left p-4 text-sm font-semibold text-neutral-400">Teléfono</th>
                <th className="text-left p-4 text-sm font-semibold text-neutral-400">Estado</th>
                <th className="text-left p-4 text-sm font-semibold text-neutral-400">Fecha</th>
                <th className="text-right p-4 text-sm font-semibold text-neutral-400">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredLeads.map((lead) => (
                <tr key={lead.id} className="border-t border-neutral-800 hover:bg-neutral-800/30">
                  <td className="p-4">
                    <div>
                      <p className="font-semibold text-white">{lead.name}</p>
                      <p className="text-xs text-neutral-500">{lead.company || '-'}</p>
                    </div>
                  </td>
                  <td className="p-4">
                    <p className="text-neutral-300">{lead.product}</p>
                    <p className="text-xs text-neutral-500">Qty: {lead.quantity || '-'}</p>
                  </td>
                  <td className="p-4">
                    <a href={`https://wa.me/${lead.phone?.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-green-400 hover:underline">
                      {lead.phone}
                    </a>
                  </td>
                  <td className="p-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${statusColors[lead.status] || statusColors.new}`}>
                      {statusLabels[lead.status] || 'Nuevo'}
                    </span>
                  </td>
                  <td className="p-4 text-sm text-neutral-400">
                    {new Date(lead.created_at).toLocaleDateString('es-ES')}
                  </td>
                  <td className="p-4">
                    <div className="flex justify-end gap-2">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => openEditDialog(lead)}
                        className="text-neutral-400 hover:text-white"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => { setLeadToDelete(lead); setShowDeleteDialog(true); }}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Lead Dialog */}
      <Dialog open={!!selectedLead} onOpenChange={() => setSelectedLead(null)}>
        <DialogContent className="bg-neutral-900 border-neutral-800">
          <DialogHeader>
            <DialogTitle>Editar Lead: {selectedLead?.name}</DialogTitle>
            <DialogDescription>
              {selectedLead?.product} - {selectedLead?.phone}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-neutral-400 mb-2 block">Estado</label>
              <Select value={editStatus} onValueChange={setEditStatus}>
                <SelectTrigger className="bg-neutral-800 border-neutral-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">Nuevo</SelectItem>
                  <SelectItem value="contacted">Contactado</SelectItem>
                  <SelectItem value="quoted">Cotizado</SelectItem>
                  <SelectItem value="converted">Convertido</SelectItem>
                  <SelectItem value="lost">Perdido</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm text-neutral-400 mb-2 block">Notas</label>
              <Textarea
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder="Añade notas sobre este lead..."
                className="bg-neutral-800 border-neutral-700 min-h-[100px]"
              />
            </div>

            {selectedLead && (
              <div className="p-4 rounded-xl bg-neutral-800/50 space-y-2 text-sm">
                <p><strong className="text-neutral-400">Origen:</strong> <span className="text-white">{selectedLead.origin || '-'}</span></p>
                <p><strong className="text-neutral-400">Destino:</strong> <span className="text-white">{selectedLead.destination || '-'}</span></p>
                <p><strong className="text-neutral-400">Mensaje:</strong> <span className="text-white">{selectedLead.message || '-'}</span></p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedLead(null)}>Cancelar</Button>
            <Button onClick={handleUpdateLead} className="bg-orange-500 hover:bg-orange-600">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-neutral-900 border-neutral-800">
          <DialogHeader>
            <DialogTitle>¿Eliminar lead?</DialogTitle>
            <DialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente el lead de {leadToDelete?.name}.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancelar</Button>
            <Button onClick={handleDeleteLead} className="bg-red-500 hover:bg-red-600">Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Content Editor Component  
function ContentEditor({ onRefresh }) {
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingContent, setEditingContent] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newContent, setNewContent] = useState({
    key: "",
    type: "text",
    value_es: "",
    value_en: "",
    section: "hero"
  });

  const fetchContents = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/content`, authHeaders());
      setContents(response.data);
    } catch (err) {
      toast.error("No se pudo cargar el contenido");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContents();
  }, [fetchContents]);

  const handleCreateContent = async () => {
    try {
      await axios.post(`${API}/admin/content`, newContent, authHeaders());
      setShowCreateDialog(false);
      setNewContent({ key: "", type: "text", value_es: "", value_en: "", section: "hero" });
      fetchContents();
      toast.success("Contenido creado");
    } catch (err) {
      toast.error("Error al crear el contenido (¿clave duplicada?)");
    }
  };

  const handleUpdateContent = async () => {
    try {
      await axios.put(`${API}/admin/content/${editingContent.key}`, {
        value_es: editingContent.value_es,
        value_en: editingContent.value_en
      }, authHeaders());
      setEditingContent(null);
      fetchContents();
      toast.success("Contenido actualizado");
    } catch (err) {
      toast.error("Error al actualizar el contenido");
    }
  };

  const handleDeleteContent = async (key) => {
    if (!window.confirm("¿Eliminar este contenido?")) return;
    try {
      await axios.delete(`${API}/admin/content/${key}`, authHeaders());
      fetchContents();
      toast.success("Contenido eliminado");
    } catch (err) {
      toast.error("Error al eliminar el contenido");
    }
  };

  const sections = ["hero", "services", "options", "process", "trust", "faq", "contact", "footer"];
  const types = ["text", "video", "image", "html"];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white font-['Outfit']">Contenido Editable</h2>
        <Button onClick={() => setShowCreateDialog(true)} className="bg-orange-500 hover:bg-orange-600 rounded-xl gap-2">
          <Plus className="w-4 h-4" />
          Nuevo Contenido
        </Button>
      </div>

      <div className="glass rounded-2xl p-6">
        <p className="text-neutral-400 mb-6">
          Aquí puedes crear y editar textos, URLs de videos e imágenes que se mostrarán en la landing page.
          Los cambios se reflejarán automáticamente cuando recargues la página.
        </p>

        {loading ? (
          <div className="text-center py-8 text-neutral-500">Cargando...</div>
        ) : contents.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-neutral-500 mb-4">No hay contenido editable aún.</p>
            <Button onClick={() => setShowCreateDialog(true)} variant="outline" className="rounded-xl">
              Crear primer contenido
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {contents.map((content) => (
              <div key={content.key} className="p-4 rounded-xl bg-neutral-800/50 border border-neutral-700">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-1 rounded bg-orange-500/20 text-orange-400 text-xs font-semibold">
                        {content.section}
                      </span>
                      <span className="px-2 py-1 rounded bg-neutral-700 text-neutral-300 text-xs">
                        {content.type}
                      </span>
                      <code className="text-xs text-neutral-500">{content.key}</code>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-neutral-500 text-xs mb-1">Español:</p>
                        <p className="text-neutral-300 line-clamp-2">{content.value_es}</p>
                      </div>
                      <div>
                        <p className="text-neutral-500 text-xs mb-1">English:</p>
                        <p className="text-neutral-300 line-clamp-2">{content.value_en}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setEditingContent(content)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-400" onClick={() => handleDeleteContent(content.key)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Content Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-neutral-900 border-neutral-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle>Crear Nuevo Contenido</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm text-neutral-400 mb-2 block">Clave (ID único)</label>
                <Input
                  value={newContent.key}
                  onChange={(e) => setNewContent({...newContent, key: e.target.value})}
                  placeholder="hero_video_url"
                  className="bg-neutral-800 border-neutral-700"
                />
              </div>
              <div>
                <label className="text-sm text-neutral-400 mb-2 block">Tipo</label>
                <Select value={newContent.type} onValueChange={(v) => setNewContent({...newContent, type: v})}>
                  <SelectTrigger className="bg-neutral-800 border-neutral-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-neutral-400 mb-2 block">Sección</label>
                <Select value={newContent.section} onValueChange={(v) => setNewContent({...newContent, section: v})}>
                  <SelectTrigger className="bg-neutral-800 border-neutral-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sections.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <label className="text-sm text-neutral-400 mb-2 block">Valor (Español)</label>
              <Textarea
                value={newContent.value_es}
                onChange={(e) => setNewContent({...newContent, value_es: e.target.value})}
                placeholder="Contenido en español..."
                className="bg-neutral-800 border-neutral-700"
              />
            </div>
            
            <div>
              <label className="text-sm text-neutral-400 mb-2 block">Value (English)</label>
              <Textarea
                value={newContent.value_en}
                onChange={(e) => setNewContent({...newContent, value_en: e.target.value})}
                placeholder="Content in English..."
                className="bg-neutral-800 border-neutral-700"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreateContent} className="bg-orange-500 hover:bg-orange-600">Crear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Content Dialog */}
      <Dialog open={!!editingContent} onOpenChange={() => setEditingContent(null)}>
        <DialogContent className="bg-neutral-900 border-neutral-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar: {editingContent?.key}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-neutral-400 mb-2 block">Valor (Español)</label>
              <Textarea
                value={editingContent?.value_es || ""}
                onChange={(e) => setEditingContent({...editingContent, value_es: e.target.value})}
                className="bg-neutral-800 border-neutral-700 min-h-[100px]"
              />
            </div>
            
            <div>
              <label className="text-sm text-neutral-400 mb-2 block">Value (English)</label>
              <Textarea
                value={editingContent?.value_en || ""}
                onChange={(e) => setEditingContent({...editingContent, value_en: e.target.value})}
                className="bg-neutral-800 border-neutral-700 min-h-[100px]"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingContent(null)}>Cancelar</Button>
            <Button onClick={handleUpdateContent} className="bg-orange-500 hover:bg-orange-600">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Pages Manager Component
function PagesManager() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPage, setEditingPage] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newPage, setNewPage] = useState({
    slug: "",
    title_es: "",
    title_en: "",
    content_es: "",
    content_en: "",
    is_published: false
  });

  const fetchPages = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/pages`, authHeaders());
      setPages(response.data);
    } catch (err) {
      toast.error("No se pudieron cargar las páginas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  const handleCreatePage = async () => {
    try {
      await axios.post(`${API}/admin/pages`, newPage, authHeaders());
      setShowCreateDialog(false);
      setNewPage({ slug: "", title_es: "", title_en: "", content_es: "", content_en: "", is_published: false });
      fetchPages();
      toast.success("Página creada");
    } catch (err) {
      toast.error("Error al crear la página (¿slug duplicado?)");
    }
  };

  const handleUpdatePage = async () => {
    try {
      await axios.put(`${API}/admin/pages/${editingPage.id}`, {
        title_es: editingPage.title_es,
        title_en: editingPage.title_en,
        content_es: editingPage.content_es,
        content_en: editingPage.content_en,
        is_published: editingPage.is_published
      }, authHeaders());
      setEditingPage(null);
      fetchPages();
      toast.success("Página actualizada");
    } catch (err) {
      toast.error("Error al actualizar la página");
    }
  };

  const handleDeletePage = async (pageId) => {
    if (!window.confirm("¿Eliminar esta página?")) return;
    try {
      await axios.delete(`${API}/admin/pages/${pageId}`, authHeaders());
      fetchPages();
      toast.success("Página eliminada");
    } catch (err) {
      toast.error("Error al eliminar la página");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white font-['Outfit']">Páginas Personalizadas</h2>
        <Button onClick={() => setShowCreateDialog(true)} className="bg-orange-500 hover:bg-orange-600 rounded-xl gap-2">
          <Plus className="w-4 h-4" />
          Nueva Página
        </Button>
      </div>

      <div className="glass rounded-2xl p-6">
        <p className="text-neutral-400 mb-6">
          Crea páginas adicionales para promociones, productos específicos, ofertas especiales, etc.
          Cada página tendrá su propia URL (ej: /page/ofertas-iphone)
        </p>

        {loading ? (
          <div className="text-center py-8 text-neutral-500">Cargando...</div>
        ) : pages.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-neutral-500 mb-4">No hay páginas personalizadas aún.</p>
            <Button onClick={() => setShowCreateDialog(true)} variant="outline" className="rounded-xl">
              Crear primera página
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {pages.map((page) => (
              <div key={page.id} className="p-4 rounded-xl bg-neutral-800/50 border border-neutral-700">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-white">{page.title_es}</h3>
                      {page.is_published ? (
                        <span className="px-2 py-0.5 rounded bg-green-500/20 text-green-400 text-xs">Publicada</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded bg-neutral-700 text-neutral-400 text-xs">Borrador</span>
                      )}
                    </div>
                    <p className="text-sm text-neutral-500">/page/{page.slug}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setEditingPage(page)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-400" onClick={() => handleDeletePage(page.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Page Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-neutral-900 border-neutral-800 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Crear Nueva Página</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-neutral-400 mb-2 block">Slug (URL)</label>
                <Input
                  value={newPage.slug}
                  onChange={(e) => setNewPage({...newPage, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-')})}
                  placeholder="ofertas-iphone-15"
                  className="bg-neutral-800 border-neutral-700"
                />
                <p className="text-xs text-neutral-500 mt-1">URL: /page/{newPage.slug || 'slug'}</p>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newPage.is_published}
                    onChange={(e) => setNewPage({...newPage, is_published: e.target.checked})}
                    className="rounded"
                  />
                  <span className="text-sm text-neutral-300">Publicar inmediatamente</span>
                </label>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-neutral-400 mb-2 block">Título (Español)</label>
                <Input
                  value={newPage.title_es}
                  onChange={(e) => setNewPage({...newPage, title_es: e.target.value})}
                  placeholder="Ofertas iPhone 15"
                  className="bg-neutral-800 border-neutral-700"
                />
              </div>
              <div>
                <label className="text-sm text-neutral-400 mb-2 block">Title (English)</label>
                <Input
                  value={newPage.title_en}
                  onChange={(e) => setNewPage({...newPage, title_en: e.target.value})}
                  placeholder="iPhone 15 Deals"
                  className="bg-neutral-800 border-neutral-700"
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm text-neutral-400 mb-2 block">Contenido (Español) - Puedes usar HTML</label>
              <Textarea
                value={newPage.content_es}
                onChange={(e) => setNewPage({...newPage, content_es: e.target.value})}
                placeholder="<h2>Oferta especial</h2><p>Descripción...</p>"
                className="bg-neutral-800 border-neutral-700 min-h-[150px] font-mono text-sm"
              />
            </div>
            
            <div>
              <label className="text-sm text-neutral-400 mb-2 block">Content (English) - You can use HTML</label>
              <Textarea
                value={newPage.content_en}
                onChange={(e) => setNewPage({...newPage, content_en: e.target.value})}
                placeholder="<h2>Special offer</h2><p>Description...</p>"
                className="bg-neutral-800 border-neutral-700 min-h-[150px] font-mono text-sm"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreatePage} className="bg-orange-500 hover:bg-orange-600">Crear Página</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Page Dialog */}
      <Dialog open={!!editingPage} onOpenChange={() => setEditingPage(null)}>
        <DialogContent className="bg-neutral-900 border-neutral-800 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Página: {editingPage?.title_es}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-4">
              <p className="text-sm text-neutral-500">URL: /page/{editingPage?.slug}</p>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editingPage?.is_published || false}
                  onChange={(e) => setEditingPage({...editingPage, is_published: e.target.checked})}
                  className="rounded"
                />
                <span className="text-sm text-neutral-300">Publicada</span>
              </label>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-neutral-400 mb-2 block">Título (Español)</label>
                <Input
                  value={editingPage?.title_es || ""}
                  onChange={(e) => setEditingPage({...editingPage, title_es: e.target.value})}
                  className="bg-neutral-800 border-neutral-700"
                />
              </div>
              <div>
                <label className="text-sm text-neutral-400 mb-2 block">Title (English)</label>
                <Input
                  value={editingPage?.title_en || ""}
                  onChange={(e) => setEditingPage({...editingPage, title_en: e.target.value})}
                  className="bg-neutral-800 border-neutral-700"
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm text-neutral-400 mb-2 block">Contenido (Español)</label>
              <Textarea
                value={editingPage?.content_es || ""}
                onChange={(e) => setEditingPage({...editingPage, content_es: e.target.value})}
                className="bg-neutral-800 border-neutral-700 min-h-[150px] font-mono text-sm"
              />
            </div>
            
            <div>
              <label className="text-sm text-neutral-400 mb-2 block">Content (English)</label>
              <Textarea
                value={editingPage?.content_en || ""}
                onChange={(e) => setEditingPage({...editingPage, content_en: e.target.value})}
                className="bg-neutral-800 border-neutral-700 min-h-[150px] font-mono text-sm"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingPage(null)}>Cancelar</Button>
            <Button onClick={handleUpdatePage} className="bg-orange-500 hover:bg-orange-600">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Main Admin Page
export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(!!getToken());
  const [activeTab, setActiveTab] = useState("dashboard");
  const [stats, setStats] = useState({});
  const [leads, setLeads] = useState([]);
  const navigate = useNavigate();

  const handleLogout = useCallback(() => {
    removeToken();
    setIsAuthenticated(false);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, leadsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, authHeaders()),
        axios.get(`${API}/admin/leads`, authHeaders())
      ]);
      setStats(statsRes.data);
      setLeads(leadsRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        handleLogout();
      }
    }
  }, [handleLogout]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated, fetchData]);

  if (!isAuthenticated) {
    return <LoginPage onLogin={() => setIsAuthenticated(true)} />;
  }

  const tabs = [
    { id: "dashboard", label: "Dashboard", icon: BarChart3 },
    { id: "leads", label: "Leads", icon: Users },
    { id: "content", label: "Contenido", icon: FileText },
    { id: "pages", label: "Páginas", icon: FileText }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 glass border-r border-neutral-800 p-4 hidden lg:block">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center overflow-hidden">
            <img src="https://customer-assets.emergentagent.com/job_3eb1f7e1-02cf-4acb-8ee2-746b00b03901/artifacts/kuo8ppes_Logo%20Global%20tech%20500x500%20FT.png" alt="GTX" className="w-full h-full object-contain" />
          </div>
          <div>
            <h1 className="font-bold text-white font-['Outfit']">Admin Panel</h1>
            <p className="text-xs text-neutral-500">Global Tech Xperts (GTX)</p>
          </div>
        </div>

        <nav className="space-y-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                activeTab === tab.id
                  ? "bg-orange-500/20 text-orange-400 border border-orange-500/30"
                  : "text-neutral-400 hover:bg-neutral-800/50 hover:text-white"
              }`}
            >
              <tab.icon className="w-5 h-5" />
              {tab.label}
            </button>
          ))}
          {STARLINK_ENABLED && (
            <button
              onClick={() => navigate("/admin/reservas")}
              data-testid="admin-starlink-btn"
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all text-blue-300 hover:bg-blue-500/15 hover:text-blue-200 border border-blue-500/30 bg-blue-500/[0.06]"
            >
              <Radio className="w-5 h-5" />
              <span className="flex-1 text-left">Reservas Starlink</span>
              <span className="text-[9px] font-bold tracking-widest uppercase bg-blue-500/25 text-blue-200 rounded px-1.5 py-0.5">NEW</span>
            </button>
          )}
        </nav>

        <div className="absolute bottom-4 left-4 right-4 space-y-2">
          <Button
            onClick={() => navigate("/")}
            variant="outline"
            className="w-full rounded-xl border-neutral-700 gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Ver Landing
          </Button>
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full rounded-xl text-red-400 hover:text-red-300 hover:bg-red-500/10 gap-2"
          >
            <LogOut className="w-4 h-4" />
            Cerrar Sesión
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden glass border-b border-neutral-800 p-4 sticky top-0 z-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-amber-400 flex items-center justify-center">
              <Globe className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-white">Admin</span>
          </div>
          <Button onClick={handleLogout} variant="ghost" size="sm" className="text-red-400">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
        
        <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? "bg-orange-500 text-white"
                  : "bg-neutral-800 text-neutral-400"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
          {STARLINK_ENABLED && (
            <button
              onClick={() => navigate("/admin/reservas")}
              data-testid="admin-starlink-btn-mobile"
              className="flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap bg-blue-500/20 text-blue-300 border border-blue-500/40"
            >
              <Radio className="w-4 h-4" />
              Starlink
            </button>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-8">
        {activeTab === "dashboard" && <Dashboard stats={stats} onRefresh={fetchData} />}
        {activeTab === "leads" && <LeadsManager leads={leads} onRefresh={fetchData} />}
        {activeTab === "content" && <ContentEditor onRefresh={fetchData} />}
        {activeTab === "pages" && <PagesManager />}
      </main>
    </div>
  );
}
